'use client'

import { AnimatePresence, m } from 'framer-motion'
import { Filter } from 'lucide-react'
import { useMemo, useState } from 'react'

import CaseCard from '@/components/CaseCard'
import {
  DIRECTION_FILTERS_WITH_ALL as FILTERS,
  directionFromCaseCategory,
  type DirectionId,
  type FilterId,
  inferDirectionFromValues,
} from '@/shared/constants/directions'

export interface CaseFilterItem {
  id: string
  title: string
  slug: string
  client: string
  description: string
  year: string | null
  tags: string | null
  category?: string | null
  coverImage: string | null
}

function detectDirection(project: CaseFilterItem): DirectionId | null {
  return (
    directionFromCaseCategory(project.category) ||
    inferDirectionFromValues([project.tags, project.title, project.description, project.client])
  )
}

export default function CasesFilter({ cases }: { cases: CaseFilterItem[] }) {
  const [activeFilter, setActiveFilter] = useState<FilterId>('all')

  const mappedCases = useMemo(() => {
    return cases.map((project) => ({
      ...project,
      direction: detectDirection(project),
    }))
  }, [cases])

  const filteredCases = useMemo(() => {
    if (activeFilter === 'all') {
      return mappedCases
    }
    return mappedCases.filter((project) => project.direction === activeFilter)
  }, [mappedCases, activeFilter])

  return (
    <div className="w-full">
      <div className="animate-fade-in-up mb-10 flex flex-col items-center gap-4 sm:mb-16 sm:gap-6">
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-gray-400">
          <Filter className="h-4 w-4" />
          <span className="home-meta text-gray-400">Фильтры</span>
        </div>

        <div className="flex w-full justify-center">
          <div className="relative isolate inline-flex w-fit max-w-full flex-wrap justify-center gap-1 rounded-[28px] border border-white/10 bg-white/5 p-2 shadow-2xl shadow-black/50 backdrop-blur-2xl sm:rounded-full">
            {FILTERS.map((filter) => {
              const isActive = activeFilter === filter.id
              return (
                <button
                  key={`cases-filter-${filter.id}`}
                  onClick={() => setActiveFilter(filter.id)}
                  className={[
                    'home-caption relative z-10 rounded-full px-4 py-2 font-medium transition-colors duration-300 md:px-5 md:py-2.5',
                    isActive ? 'text-black' : 'text-gray-400 hover:text-white',
                  ].join(' ')}
                >
                  {isActive && (
                    <m.div
                      layoutId="activeTagFilter"
                      className="absolute inset-0 -z-10 rounded-full bg-white shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 font-medium tracking-wide whitespace-nowrap uppercase">
                    {filter.label}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <AnimatePresence mode="popLayout">
        <div className="grid grid-cols-1 items-stretch gap-6 sm:gap-8 md:grid-cols-2 xl:grid-cols-3">
          {filteredCases.map((project, index) => (
            <m.div
              key={project.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              layout
              className="h-full"
            >
              <CaseCard project={project} index={index} />
            </m.div>
          ))}
        </div>
      </AnimatePresence>

      {filteredCases.length === 0 && (
        <div className="home-subnote py-20 text-center text-gray-500">
          Проектов в этой категории пока нет.
        </div>
      )}
    </div>
  )
}
